<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Text, Input, CardGrid, SelectCard } from '@Shelly/shelly-design-system';
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
	import { _init_notifications } from '../../../../../../helpers/init_notification_actions';
	import {
		is_notification_action_ui,
		notificationTypeSupportsRecipients,
	} from '../../../../../../../../types/NotificationActionUI';
	import {
		actionIncludesOwner,
		channelForNotificationType,
		ensureSubUnitsLoaded,
		getDefaultSubUnitRecipientsForNewAction,
		getEligibleSubUnitRecipients,
		hasShareableHome,
		reconcileSubUnitRecipients,
	} from '../../../../../../helpers/notification_recipients_helpers';
	import PhoneNotification from './Components/PhoneNotificationV2.svelte';
	import AlexaNotification from './Components/AlexaNotificationV2.svelte';
	import RecipientSelector from './Components/RecipientSelector.svelte';
	import { capFirstChar, _deepClone } from '../../../../../../../../helpers/utilities';
	import { notify_alexa } from 'src/stores/lists/scenes-derived';
	import { sub_units } from 'src/stores/account/home';
	import { DO_WIZARD_STEPS_INDEXES } from '../../helpers/do-wizard-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let sceneData: any;

	const notifications_options: SelectOptionPropsType[] = _init_notifications.map((el) => {
		return {
			title: capFirstChar($format(el.title)),
			value: el.props.id,
		};
	});

	const mappedNotificationTypeData = {
		0: {
			icon: 'fal fa-mobile-signal-out',
			desc: $format('description_notify_phone', {
				default:
					'You will receive a notification when the scene is triggered (‘Smart Control’ app required).',
			}),
		},
		1: {
			icon: 'fal fa-envelope',
			desc: $format('description_notify_email', {
				default: 'You will receive an e-mail notification when the scene is triggered.',
			}),
		},
		2: {
			icon: 'fal fa-notebook',
			desc: $format('description_notify_activity_log', {
				default: 'Your scene will be added to the activity log as soon as it gets triggered.',
			}),
		},
		3: {
			icon: 'fa-brands fa-amazon',
			desc: $format('description_notify_alexa', {
				default: 'Send an event to Amazon Alexa (could be used as a trigger for Alexa Routines)',
			}),
		},
	};

	let selectedNotification = {
		title: 'Select notification',
		value: undefined,
	};

	$: alexaHasNotifyAction =
		sceneData.scene_object?._meta?.integrations ||
		$notify_alexa._active_cnt === $notify_alexa._max ||
		sceneData.notificationAction.already_in_current_scene;

	$: (sceneData, $sub_units, validateWizardStepChangeState());

	/**
	 * The recipient selector is shown only when:
	 * - the notification type supports per-recipient delivery (push / email), AND
	 * - the home has at least one shared sub-unit (progressive disclosure per FE-1682).
	 */
	$: showRecipientSelector =
		notificationTypeSupportsRecipients(sceneData?.notificationAction?.type) &&
		(void $sub_units, hasShareableHome());

	const validateWizardStepChangeState = () => {
		sceneData.isValid = false;
		sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.NOTIFY_ACTION_STEP;

		if (selectedNotification.value !== 'alexa_notif') {
			const baseValid =
				sceneData.notificationAction.type !== '' &&
				sceneData.notificationAction.msg_type !== '' &&
				sceneData.notificationAction.msg_type !== undefined &&
				sceneData.notificationAction.delay.value >= 0 &&
				(selectedNotification.value === 'mail_notification' ||
				selectedNotification.value === 'push_notification'
					? sceneData.notificationAction.msg !== ''
					: true);

			sceneData.isValid = baseValid && hasValidRecipientsForCurrentType();
		} else {
			sceneData.isValid = !alexaHasNotifyAction;
		}
	};

	/**
	 * Recipient validation rules:
	 * - For non-recipient notification types (event log / Alexa), recipients are irrelevant.
	 * - When the home is not shared, only the owner can ever receive — valid.
	 * - When the home is shared, the action must reach at least one of:
	 *   the owner (`!action.pushSkipUnit`) or at least one eligible sub-unit.
	 *   Orphan-only selections (ids that were removed from the home) do not count.
	 */
	const hasValidRecipientsForCurrentType = (): boolean => {
		const channel = channelForNotificationType(sceneData?.notificationAction?.type);
		if (!channel) return true;
		// Touch the store so this getter re-runs when sub_units change.
		void $sub_units;
		if (!hasShareableHome()) return true;
		const ownerIncluded = actionIncludesOwner(sceneData?.notificationAction);
		const { selected } = reconcileSubUnitRecipients(sceneData?.notificationAction?.toSub);
		return ownerIncluded || selected.length > 0;
	};

	/**
	 * Migrates a legacy action (no `toSub`) into the new explicit shape on
	 * first edit: every current sub-unit is preselected, mirroring the old
	 * "all members" behavior. The user can then narrow down before saving.
	 */
	const hydrateRecipientsOnEdit = (): void => {
		const channel = channelForNotificationType(sceneData?.notificationAction?.type);
		if (!channel) return;
		if (Array.isArray(sceneData.notificationAction.toSub)) return;
		sceneData.notificationAction = {
			...sceneData.notificationAction,
			toSub: getEligibleSubUnitRecipients(channel).map((r) => r.subId),
		};
	};

	/**
	 * Ensures the action has an explicit `toSub` field once the user picks a
	 * push/mail type, so the wizard validation can rely on it instead of the
	 * legacy `undefined` semantics.
	 */
	const seedDefaultRecipientsIfNeeded = (): void => {
		const channel = channelForNotificationType(sceneData?.notificationAction?.type);
		if (!channel) return;
		if (Array.isArray(sceneData.notificationAction.toSub)) return;
		sceneData.notificationAction = {
			...sceneData.notificationAction,
			toSub: getDefaultSubUnitRecipientsForNewAction(),
		};
	};

	if (is_notification_action_ui(sceneData.translated)) {
		sceneData.notificationAction = _deepClone(sceneData.translated);
		selectedNotification = notifications_options.find(
			(el) => el.value === sceneData.notificationAction.type,
		) || {
			title: 'unknown',
			value: undefined,
		};

		if (sceneData.translated.delay === undefined) {
			sceneData.notificationAction['delay'] = {
				value: 0,
				units: 'sec',
			};
		}

		// FE-1682: hydrate the recipient list when editing an existing action.
		// Legacy actions without `to` get migrated to "all eligible recipients"
		// so the wizard reflects current behavior; the user can then narrow down.
		hydrateRecipientsOnEdit();
	}

	onMount(() => {
		// FE-1682: refresh the sub-unit roster on every wizard open so newly
		// invited members (added from the Sharing screen, or via another
		// tab / device) show up in the recipient selector immediately —
		// without requiring a full page reload. Cached entries stay visible
		// while the refresh is in flight, so there is no empty flash.
		// Sub-unit sessions cannot call this endpoint, so we skip there.
		void ensureSubUnitsLoaded();
		// When the recipient selector is in scope but the wizard step rendered
		// without a `to` field (e.g. mid-flow type switch), seed defaults so the
		// validation passes and the user sees a meaningful pre-selection.
		seedDefaultRecipientsIfNeeded();
	});

	const change_selected = (e) => {
		selectedNotification = { ...e.detail };
		let activeInd = _init_notifications.findIndex(
			(el) => el.props.id === selectedNotification.value,
		);
		//on change selected notification type update data and reset message
		sceneData.notificationAction = {
			id: activeInd,
			name: selectedNotification.title,
			msg_type: '',
			msg: _init_notifications[activeInd]?.props?.msg
				? $format('notification_msg', { default: 'My scene triggered' })
				: '',
			type: selectedNotification.value,
			delay: {
				value: 0,
				units: 'sec',
			},
			/**
			 * FE-1682: seed `toSub` with the empty default for newly chosen
			 * push/mail types. Owner delivery is controlled per-action via
			 * `pushSkipUnit` and stays opt-in by default.
			 */
			...(notificationTypeSupportsRecipients(selectedNotification.value)
				? { toSub: getDefaultSubUnitRecipientsForNewAction() }
				: {}),
		};

		if (
			selectedNotification.value === 'event_log' ||
			selectedNotification.value === 'mail_notification' ||
			selectedNotification.value === 'push_notification'
		) {
			let action =
				_init_notifications.find((el) => el?.props?.id === selectedNotification.value) ||
				undefined;

			sceneData.notificationAction.msg_type =
				sceneData.notificationAction.msg_type === action?.props?.values[0]?.msg_type
					? undefined
					: action?.props?.values[0]?.msg_type;
		} else if (selectedNotification.value === 'alexa_notif') {
			sceneData.notificationAction.msg_type =
				$notify_alexa._not_premium_cnt < $notify_alexa._max_not_premium
					? sceneData.notificationAction.msg_type === 'alexa_notif'
						? undefined
						: 'alexa_notif'
					: sceneData.notificationAction.msg_type === 'alexa_notif_premium'
						? undefined
						: 'alexa_notif_premium';
		}
	};
</script>

<Text element="h2" class="mb-s">
	{$format('select_notification_type')}
</Text>

<CardGrid gridGap="s" lg={290} sm={270} noPaddingBottom>
	{#each notifications_options as option, _i}
		<div class="d-flex">
			<SelectCard
				cardBgColor={$showNewLayout ? 'color-1' : undefined}
				class="width-full"
				type="horizontal"
				variant="radio"
				title={option.title}
				descriptiveText={mappedNotificationTypeData[_i].desc}
				icon={mappedNotificationTypeData[_i].icon}
				selected={selectedNotification.value === option.value}
				on:click={() => {
					change_selected({ detail: option });
				}}
			/>
		</div>
	{/each}
</CardGrid>

{#if selectedNotification.value === 'push_notification'}
	<PhoneNotification bind:sceneData />
{:else if selectedNotification.value === 'mail_notification'}
	<Text element="h2" class="mb-s mt-l">
		{$format('notification_settings_title', {
			default: 'Notification settings',
		})}
	</Text>
	<Input
		aria-label={$format('input_email_notification', { default: 'Email notification message' })}
		size="m"
		bind:value={sceneData.notificationAction.msg}
		icon="fas fa-bell"
		info={$format('phone_notification_example', {
			default: 'Example: My scene is triggered!',
		})}
		id="input-x16bwdv"
	/>
{:else if selectedNotification.value === 'alexa_notif'}
	<AlexaNotification bind:sceneData />
{:else if selectedNotification.value === 'event_log'}
	<!-- No config logic -->
{/if}

<!-- FE-1682: per-recipient delivery for push/mail notifications on shared homes. -->
{#if showRecipientSelector}
	<RecipientSelector bind:sceneData />
{/if}
